<template>
  <div class="assignment-filter-sidebar uk-width-expand">
    <div class="uk-sticky" uk-sticky="offset: 100; media: @s; bottom: true">
      <div class="sidebar-header">
        <slot name="header">
          <h4 class="filter-title">
            <i class="il-filter"></i> {{ $t('general.filter') }}
          </h4>
        </slot>
      </div>
      <div class="sidebar-filter">
        <ul class="sidebar-filter-list uk-accordion" uk-accordion="multiple: true">
          <li :class="{ 'uk-open': isTagFilterOpen }">
            <a class="uk-accordion-title" href="#">
              {{ $t('general.by_category_tag') }}
            </a>
            <div class="uk-accordion-content">
              <div class="search-section">
                <label for="search-tag" class="search-label">
                  <i class="uil-search"></i>{{ $t('general.Tag Search') }}
                </label>
                <input
                    ref="searchInput"
                    v-model="localSearchTag"
                    autocomplete="off"
                    class="search-input"
                    :placeholder="$t('general.Tag Search') || 'Bir etiket yazın'"
                    type="text"
                    id="search-tag"
                    @input="onSearchTagChange"
                />
              </div>
              <div class="tags-list">
                <!-- Skeleton Loading -->
                <div v-if="loading" class="skeleton-tags-list">
                  <div v-for="n in 8" :key="`skeleton-tag-${n}`" class="skeleton-tag-item">
                    <div class="skeleton-checkbox"></div>
                    <div class="skeleton-tag-name"></div>
                  </div>
                </div>
                <!-- No Tags Available (when API returns empty array) -->
                <div v-else-if="!loading && filteredTags.length === 0 && !localSearchTag" class="no-results-message">
                  <i class="uil-tag"></i>
                  <p>{{ $t('general.no_tags_available') || 'Etiket bulunamadı' }}</p>
                </div>
                <!-- No Results Message (when search returns no results) -->
                <div v-else-if="!loading && filteredTags.length === 0 && localSearchTag" class="no-results-message">
                  <i class="uil-search"></i>
                  <p>{{ $t('general.no_result_found') || 'Sonuç bulunamadı' }}</p>
                </div>
                <!-- Actual Tags -->
                <template v-else-if="!loading && filteredTags.length > 0">
                  <label 
                    v-for="item in filteredTags" 
                    :key="`tag-${item.id}`"
                    :class="{ 'tag-selected': selectedHashtag === item.id }"
                    class="tag-filter-item"
                  >
                    <input
                        :id="`hashtag-${item.id}`"
                        :checked="selectedHashtag === item.id"
                        :value="item.id"
                        class="uk-checkbox"
                        type="checkbox"
                        @change="onTagChange(item.id)"
                    />
                    <span class="tag-name">#{{ item.name }}</span>
                    <span v-if="selectedHashtag === item.id" class="tag-selected-indicator">
                      <i class="uil-check"></i>
                    </span>
                  </label>
                </template>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AssignmentFilterSidebar",
  props: {
    searchTag: {
      type: String,
      default: ''
    },
    filteredTags: {
      type: Array,
      default: () => []
    },
    selectedHashtag: {
      type: [Number, String],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localSearchTag: this.searchTag,
      isTagFilterOpen: typeof window === 'undefined' ? true : window.innerWidth > 959
    }
  },
  watch: {
    searchTag(newVal) {
      this.localSearchTag = newVal
    }
  },
  methods: {
    onSearchTagChange() {
      this.$emit('search-tag-change', this.localSearchTag)
    },
    onTagChange(hashtagId) {
      const newValue = this.selectedHashtag === hashtagId ? null : hashtagId
      this.$emit('hashtag-change', newValue)
    }
  }
}
</script>

<style scoped>
.assignment-filter-sidebar {
  width: 100%;
}

.sidebar-header {
  margin-bottom: 16px;
}

.filter-title {
  text-transform: capitalize;
  margin-bottom: 0;
}

/* Slot içinden gelen widget dropdown */
:deep(.sidebar-header .widget-select) {
  width: 100%;
}

:deep(.sidebar-header .widget-select .form-group) {
  margin-bottom: 0;
}

/* Uzun başlık seçildiğinde tek satırda kalsın, taşan kısım ... ile kesilsin */
:deep(.sidebar-header .widget-select .multiselect__tags) {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  overflow: hidden;
}

:deep(.sidebar-header .widget-select .multiselect__single) {
  flex: 1 1 auto;
  min-width: 0;
  margin-bottom: 0;
  padding-right: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Tıklanınca açılan liste biraz daha uzun görünsün */
:deep(.sidebar-header .widget-select .multiselect__content-wrapper),
:deep(.sidebar-header .widget-select .multiselect__content) {
  max-height: 280px !important;
}

:deep(.sidebar-header .widget-select .multiselect__content-wrapper) {
  border: 1px solid #e2e8f0;
  border-top: none;
  border-radius: 0 0 10px 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.sidebar-filter {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: none;
}

/* Search Section */
.search-section {
  margin-bottom: 16px;
}

.search-label {
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 8px;
  display: block;
}

.search-label i {
  margin-right: 6px;
  color: var(--primaryColor, var(--iconColor, #3e416d));
}

.search-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background-color: #fff;
  color: #333;
  display: block;
}

.search-input:focus,
.search-input:focus-visible {
  outline: none;
  border: 1px solid var(--primaryColor, var(--iconColor, #3e416d)) !important;
  background-color: #fff;
}

.search-input::placeholder {
  color: #999;
  font-size: 13px;
}

.search-input:hover {
  border-color: #9ca3af;
}

@media (max-width: 768px) {
  .search-input:hover {
    border-color: #d1d5db;
  }
}

/* Tags List */
.tags-list {
  overflow-y: auto;
  max-height: 30rem;
}

.tag-filter-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  margin-bottom: 2px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 10px;
  border: 1px solid transparent;
}

.tag-filter-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
  border-color: #e2e8f0;
}

.tag-filter-item.tag-selected {
  background-color: rgba(62, 65, 109, 0.06);
  border-color: var(--primaryColor, var(--iconColor, #3e416d));
}

@media (max-width: 768px) {
  .tag-filter-item:hover {
    background-color: transparent;
    border-color: transparent;
  }

  .tag-filter-item:active {
    background-color: rgba(0, 0, 0, 0.04);
  }

  .tag-filter-item.tag-selected {
    background-color: rgba(62, 65, 109, 0.06);
    border-color: var(--primaryColor, var(--iconColor, #3e416d));
  }
}

.tag-filter-item.tag-selected .tag-name {
  color: var(--primaryColor, var(--iconColor, #3e416d));
  font-weight: 500;
}

.tag-name {
  flex: 1;
  margin-left: 8px;
  user-select: none;
  font-weight: 300;
}

.tag-selected-indicator {
  margin-left: auto;
  color: var(--primaryColor, var(--iconColor, #3e416d));
  font-size: 16px;
  font-weight: bold;
}

/* Checkbox Styling */
.sidebar-filter .uk-checkbox {
  border: 1px solid #e2e8f0;
  cursor: pointer;
  border-radius: 6px;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  transition: border-color 0.2s ease;
}

.sidebar-filter .uk-checkbox:hover {
  border-color: var(--primaryColor, var(--iconColor, #3e416d));
}

@media (max-width: 768px) {
  .sidebar-filter .uk-checkbox:hover {
    border-color: #e2e8f0;
  }
}

.sidebar-filter .uk-checkbox:checked {
  background-color: var(--primaryColor, var(--iconColor, #3e416d));
  border-color: var(--primaryColor, var(--iconColor, #3e416d));
  border-radius: 6px;
}

.sidebar-filter .uk-checkbox:checked:focus {
  background-color: var(--primaryColor, var(--iconColor, #3e416d));
  border-color: var(--primaryColor, var(--iconColor, #3e416d));
}

/* Skeleton Loading Styles */
.skeleton-tags-list {
  overflow-y: auto;
  max-height: 30rem;
}

.skeleton-tag-item {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  margin-bottom: 2px;
}

.skeleton-checkbox {
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
  margin-right: 8px;
}

.skeleton-tag-name {
  flex: 1;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
  margin-left: 8px;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* No Results Message */
.no-results-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: #666;
}

.no-results-message i {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 12px;
}

.no-results-message p {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 8px 0;
}

.no-results-hint {
  font-size: 13px;
  color: #999;
}
</style>


